import type {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  MutableRefObject,
} from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  deleteStoredTalentPoolCV,
  storeTalentPoolCV,
} from '@/lib/indexed-db'
import {
  isAcceptedClientPdfFile,
  isPdfFileName,
} from '@/lib/files/pdf'

import type { talentPoolCopy } from '@/app/talent-pool/copy'
import { pushTalentPoolGtmEvent } from './talent-pool-analytics'
import type { UploadPickerTrigger } from './talent-pool-page-types'

const getFileExtension = (fileName: string) => {
  const parts = fileName.split('.')
  if (parts.length < 2) return 'unknown'
  return parts[parts.length - 1]?.toLowerCase() ?? 'unknown'
}

type UploadValidationCopy = typeof talentPoolCopy.validation

type UseTalentPoolUploadStateOptions = {
  validationCopy: UploadValidationCopy
  storedFileName: string | null
  setStoredFileName: (name: string | null) => void
  setSubmitError: (message: string) => void
}

export type TalentPoolUploadState = {
  file: File | null
  setFile: (file: File | null) => void
  isDragging: boolean
  canGoToPersonal: boolean
  fileInputRef: MutableRefObject<HTMLInputElement | null>
  handleUploadAreaDragOver: (event: DragEvent<HTMLDivElement>) => void
  handleUploadAreaDragLeave: () => void
  handleUploadAreaDrop: (event: DragEvent<HTMLDivElement>) => void
  openFilePicker: (trigger: UploadPickerTrigger) => void
  handleUploadAreaKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  handleFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  removeFile: () => Promise<void>
}

export const useTalentPoolUploadState = ({
  validationCopy,
  storedFileName,
  setStoredFileName,
  setSubmitError,
}: UseTalentPoolUploadStateOptions): TalentPoolUploadState => {
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!storedFileName || isPdfFileName(storedFileName)) return

    setStoredFileName(null)
    void deleteStoredTalentPoolCV()
  }, [setStoredFileName, storedFileName])

  const validateFile = useCallback(
    (selectedFile: File): string | null => {
      if (!isAcceptedClientPdfFile(selectedFile)) return validationCopy.fileType
      if (selectedFile.size > 5 * 1024 * 1024) return validationCopy.fileSize
      return null
    },
    [validationCopy.fileSize, validationCopy.fileType],
  )

  const handleFileSelect = useCallback(
    async (selectedFile: File, source: 'picker' | 'drop') => {
      pushTalentPoolGtmEvent('talent_pool_upload_attempt', {
        page: 'talent-pool',
        placement: 'landing',
        source,
        file_extension: getFileExtension(selectedFile.name),
        file_size_kb: Math.round(selectedFile.size / 1024),
      })

      const error = validateFile(selectedFile)
      if (error) {
        pushTalentPoolGtmEvent('talent_pool_upload_validation_error', {
          page: 'talent-pool',
          placement: 'landing',
          source,
          error_message: error,
        })
        setSubmitError(error)
        return
      }

      setSubmitError('')
      setFile(selectedFile)
      setStoredFileName(selectedFile.name)
      await storeTalentPoolCV({ file: selectedFile })

      pushTalentPoolGtmEvent('talent_pool_upload_success', {
        page: 'talent-pool',
        placement: 'landing',
        source,
        file_extension: getFileExtension(selectedFile.name),
        file_size_kb: Math.round(selectedFile.size / 1024),
      })
    },
    [setStoredFileName, setSubmitError, validateFile],
  )

  const handleUploadAreaDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragging(false)

      const droppedFile = event.dataTransfer.files.item(0)
      if (!droppedFile) return

      pushTalentPoolGtmEvent('talent_pool_upload_drop', {
        page: 'talent-pool',
        placement: 'landing',
      })
      void handleFileSelect(droppedFile, 'drop')
    },
    [handleFileSelect],
  )

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0]
      if (selectedFile) void handleFileSelect(selectedFile, 'picker')
      event.target.value = ''
    },
    [handleFileSelect],
  )

  const removeFile = useCallback(async () => {
    pushTalentPoolGtmEvent('talent_pool_upload_remove_click', {
      page: 'talent-pool',
      placement: 'landing',
      had_file: Boolean(file || storedFileName),
    })

    setFile(null)
    setStoredFileName(null)
    await deleteStoredTalentPoolCV()

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [file, setStoredFileName, storedFileName])

  const openFilePicker = useCallback((trigger: UploadPickerTrigger) => {
    pushTalentPoolGtmEvent('talent_pool_upload_picker_open_click', {
      page: 'talent-pool',
      placement: 'landing',
      trigger,
    })
    fileInputRef.current?.click()
  }, [])

  const handleUploadAreaKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      openFilePicker('keyboard')
    },
    [openFilePicker],
  )

  const handleUploadAreaDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragging(true)
    },
    [],
  )

  const handleUploadAreaDragLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  return {
    file,
    setFile,
    isDragging,
    canGoToPersonal: Boolean(file || storedFileName),
    fileInputRef,
    handleUploadAreaDragOver,
    handleUploadAreaDragLeave,
    handleUploadAreaDrop,
    openFilePicker,
    handleUploadAreaKeyDown,
    handleFileInputChange,
    removeFile,
  }
}
